import React from 'react';
import { Alert, Empty, Typography } from 'antd';
import AiAgreementIndicator from '../AiAgreementIndicator';
import AiDraftCard from './AiDraftCard';
import { describeFetchRefusal } from '../../utils/questionBankCuratorModel';

const { Text, Title } = Typography;

/**
 * Result of one POST /ai-curate: why no agreement score exists for the run, fetch outcomes, candidates the server
 * discarded (and why), and the created drafts for human review. `drafts` are re-fetched by the parent
 * after every approve / reject / edit, so the cards always show the server's current state.
 */
const AiRunResults = ({ result, drafts, standardId, onChanged }) => {
    const { run, rejectedCandidates = [], fetchReport = [], notice } = result;
    return (
        <div data-testid="ai-run-results">
            <Title level={5} style={{ marginTop: 0 }}>Latest AI run</Title>
            {notice && <Alert type="info" message={notice} style={{ marginBottom: 12 }} />}
            <div style={{ marginBottom: 12 }}>
                <Text>
                    Target clause <Text strong>{run.targetClause}</Text>: {run.candidateCount} candidate(s) proposed, {run.draftsCreated} kept as draft(s)
                    {run.rejectedCount > 0 ? `, ${run.rejectedCount} discarded` : ''}.
                </Text>
                <div><Text type="secondary" style={{ fontSize: 12 }}>Model {run.modelId} | prompt {run.promptId} | request {run.requestId}</Text></div>
            </div>
            <div style={{ marginBottom: 12 }}>
                <AiAgreementIndicator
                    confidence={null}
                    unavailableReason={run.confidenceUnavailableReason}
                    testId="ai-run-confidence-tag"
                />
            </div>

            {fetchReport.length > 0 && (
                <div style={{ marginBottom: 12 }} data-testid="fetch-report">
                    <Text strong>URL sources</Text>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                        {fetchReport.map((r) => (
                            <li key={r.ref}>
                                {r.ok
                                    ? `${r.ref}: fetched ${r.finalUrl || r.url}${r.truncated ? ' (long page; only the first part was used)' : ''}`
                                    : `${r.ref}: ${describeFetchRefusal(r)}`}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {rejectedCandidates.length > 0 && (
                <Alert
                    type="warning"
                    style={{ marginBottom: 12 }}
                    data-testid="rejected-candidates"
                    message={`${rejectedCandidates.length} proposed question(s) were discarded by the server checks`}
                    description={(
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {rejectedCandidates.map((c) => (
                                <li key={c.index}>{c.question ? `"${c.question}"` : `Candidate ${c.index + 1}`}: {c.reason}</li>
                            ))}
                        </ul>
                    )}
                />
            )}

            {drafts.length === 0 ? (
                <Empty description="No usable questions came out of this run. Try a narrower clause or more complete source text." />
            ) : (
                drafts.map((d) => <AiDraftCard key={d.id} draft={d} standardId={standardId} onChanged={onChanged} />)
            )}
        </div>
    );
};

export default AiRunResults;
